import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import s from "../clinic.module.css";

function menuStyleFromAnchor(anchor) {
  if (!anchor || typeof anchor.getBoundingClientRect !== "function") {
    return { top: 80, right: 24 };
  }
  const rect = anchor.getBoundingClientRect();
  const gap = 6;
  const menuWidth = 200;
  let top = rect.bottom + gap;
  let right = Math.max(8, window.innerWidth - rect.right);
  if (top + 160 > window.innerHeight) {
    top = Math.max(8, rect.top - gap - 120);
  }
  if (right + menuWidth > window.innerWidth) {
    right = 8;
  }
  return { top, right };
}

export default function ChatMessageActionSheet({
  message,
  open,
  busy = false,
  canEdit = true,
  anchor = null,
  onClose,
  onEdit,
  onDelete,
}) {
  const isMobile = useIsMobile(768);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    if (open) setConfirmDelete(false);
  }, [open, message?.id]);

  useLayoutEffect(() => {
    if (!open || isMobile) return undefined;
    const update = () => setPos(menuStyleFromAnchor(anchor));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, isMobile, anchor, message?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    if (isMobile) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, busy, onClose, isMobile]);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointer = (e) => {
      if (busy) return;
      const menu = menuRef.current;
      if (menu?.contains(e.target)) return;
      if (anchor && typeof anchor.contains === "function" && anchor.contains(e.target)) {
        return;
      }
      onClose?.();
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, isMobile, busy, onClose, anchor]);

  if (!open || !message) return null;

  const handleDelete = () => {
    if (confirmDelete) {
      onDelete?.(message);
      return;
    }
    setConfirmDelete(true);
  };

  const menuBody = confirmDelete ? (
    <>
      <p className={isMobile ? s.postActionSheetWarn : s.postActionPopoverWarn}>
        Удалить сообщение? Его увидят как удалённое.
      </p>
      <button
        type="button"
        role={isMobile ? undefined : "menuitem"}
        className={
          isMobile
            ? `${s.postActionSheetBtn} ${s.postActionSheetBtnDanger}`
            : `${s.postActionPopoverItem} ${s.postActionPopoverItemDanger}`
        }
        onClick={handleDelete}
        disabled={busy}
      >
        {busy ? "Удаляем…" : "Удалить сообщение"}
      </button>
      <button
        type="button"
        role={isMobile ? undefined : "menuitem"}
        className={isMobile ? s.postActionSheetBtn : s.postActionPopoverItem}
        onClick={() => setConfirmDelete(false)}
        disabled={busy}
      >
        Назад
      </button>
    </>
  ) : (
    <>
      {canEdit ? (
        <button
          type="button"
          role={isMobile ? undefined : "menuitem"}
          className={isMobile ? s.postActionSheetBtn : s.postActionPopoverItem}
          onClick={() => onEdit?.(message)}
          disabled={busy}
        >
          Редактировать
        </button>
      ) : null}
      <button
        type="button"
        role={isMobile ? undefined : "menuitem"}
        className={
          isMobile
            ? `${s.postActionSheetBtn} ${s.postActionSheetBtnDanger}`
            : `${s.postActionPopoverItem} ${s.postActionPopoverItemDanger}`
        }
        onClick={handleDelete}
        disabled={busy}
      >
        Удалить
      </button>
    </>
  );

  if (!isMobile) {
    return createPortal(
      <div
        ref={menuRef}
        className={s.postActionPopover}
        style={{ top: pos.top, right: pos.right }}
        role="menu"
        aria-label="Действия с сообщением"
      >
        {menuBody}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={s.postActionSheetOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Действия с сообщением"
      onClick={() => !busy && onClose?.()}
    >
      <div
        className={s.postActionSheet}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.postActionSheetHandle} aria-hidden />
        {menuBody}
        <button
          type="button"
          className={`${s.postActionSheetBtn} ${s.postActionSheetBtnCancel}`}
          onClick={onClose}
          disabled={busy}
        >
          Отмена
        </button>
      </div>
    </div>,
    document.body,
  );
}
